/**
 * Astrixy Consent Management Platform — core types.
 *
 * Single source of truth for consent categories, policy/consent versioning
 * and the persisted consent record shape.
 */

export const CONSENT_CATEGORIES = [
  "necessary",
  "functional",
  "analytics",
  "marketing",
  "personalization",
  "social",
  "experimental",
] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

export type ConsentState = Record<ConsentCategory, boolean>;

/** Bump when the *mechanics* of consent change (categories, vendors, storage). */
export const CONSENT_VERSION = 2;

/** Bump when the Privacy / Cookie Policy text materially changes. */
export const POLICY_VERSION = "2026-07-29";

/** Consent lifetime. GDPR guidance: re-prompt at most every 6–12 months. */
export const CONSENT_TTL_DAYS = 180;

export const STORAGE_KEY = "astrixy_consent_v2";
/** Legacy key kept for one-way migration only. */
export const LEGACY_STORAGE_KEY = "cookie-preferences";

export type ConsentMethod =
  | "accept_all"
  | "reject_all"
  | "custom"
  | "gpc_signal"
  | "dnt_signal"
  | "withdrawn"
  | "expired_reprompt";

export interface ConsentRecord {
  /** Stable pseudonymous id for this browser's consent trail. */
  consentId: string;
  categories: ConsentState;
  consentVersion: number;
  policyVersion: string;
  method: ConsentMethod;
  /** ISO timestamp of the decision. */
  timestamp: string;
  /** ISO timestamp after which the banner is shown again. */
  expiresAt: string;
  signals: {
    gpc: boolean;
    dnt: boolean;
  };
  language: string;
  timeZone: string;
  device: "mobile" | "tablet" | "desktop";
}

export const DENIED_STATE: ConsentState = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  personalization: false,
  social: false,
  experimental: false,
};

export const GRANTED_STATE: ConsentState = {
  necessary: true,
  functional: true,
  analytics: true,
  marketing: true,
  personalization: true,
  social: true,
  experimental: true,
};

export interface CategoryMeta {
  id: ConsentCategory;
  label: string;
  summary: string;
  description: string;
  alwaysOn?: boolean;
}

export const CATEGORY_META: CategoryMeta[] = [
  {
    id: "necessary",
    label: "Strictly necessary",
    summary: "Required for the platform to work.",
    description:
      "These cookies keep you signed in, protect your session, route checkout requests and defend against fraud and abuse. They cannot be switched off because the platform will not function without them.",
    alwaysOn: true,
  },
  {
    id: "functional",
    label: "Functional",
    summary: "Remembers your preferences.",
    description:
      "Stores choices such as language, currency, theme, dashboard layout and dismissed tips so the interface behaves the way you left it.",
  },
  {
    id: "analytics",
    label: "Analytics",
    summary: "Helps us understand product usage.",
    description:
      "Aggregated, pseudonymous measurement of page views, feature usage and funnel drop-off. Used only to improve reliability and product design — never sold.",
  },
  {
    id: "marketing",
    label: "Marketing",
    summary: "Measures and personalises advertising.",
    description:
      "Advertising and conversion measurement identifiers that let us understand which campaigns bring merchants to Astrixy and limit how often an ad is shown.",
  },
  {
    id: "personalization",
    label: "Personalization",
    summary: "Tailors content and recommendations.",
    description:
      "Powers recommended products, tailored onboarding paths and AI suggestions based on how you use the platform.",
  },
  {
    id: "social",
    label: "Social media",
    summary: "Enables embedded social content.",
    description:
      "Allows embedded posts, share widgets and social sign-in providers to load. These vendors may set their own cookies when the content is displayed.",
  },
  {
    id: "experimental",
    label: "Experimental features",
    summary: "Opt in to A/B tests and previews.",
    description:
      "Assigns you to experiments and early-access feature flags so we can validate new functionality before general release. Entirely optional.",
  },
];
